import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { useTransition, animated } from "react-spring";
import * as d3 from "d3";

function getGlobalMax(dataArray) {
    let max = 0;
    dataArray.forEach((item) => {
        const localMax = Math.max(...item.data);
        if (localMax > max) max = localMax;
    });
    return max;
}

function BarChartRace({ data, startDate, duration, kombiniert }) {
    // Zustände für Animation, Daten und Fenstergröße
    const [time, setTime] = useState(0);
    const [formattedData, setFormattedData] = useState([]);
    const [globalMax, setGlobalMax] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    // Zustände für gemessene Layout-Größen
    const [leftLabelWidth, setLeftLabelWidth] = useState(0);
    const [rightLabelWidth, setRightLabelWidth] = useState(0);
    const [barStart, setBarStart] = useState(0);
    const [barEnd, setBarEnd] = useState(0);
    const [barAreaWidth, setBarAreaWidth] = useState(0);

    // Refs für die unsichtbaren Container zum Messen der Labels und Werte
    const leftHiddenContainerRef = useRef(null);
    const rightHiddenContainerRef = useRef(null);

    // Grundlegende Layout-Parameter
    const baseWidth = 800; // Gesamte SVG-Breite
    const margin = { top: 20, right: 20, bottom: 20, left: 20 };
    const barHeight = 30;
    const barGap = 15;
    // Abstände zwischen internem Text und Balken
    const leftGap = 20;
    const rightGap = 10;
    const computedFontSize = windowWidth < 768 ? 24 : 16;
    // Außenseitiges Padding (z. B. 1rem ≙ 16px)
    const outerPadding = 16;

    // Fenstergröße überwachen
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Daten filtern (z.B. "Soll" ausschließen) und globalen Maximalwert bestimmen
    useEffect(() => {
        const filteredData = data.filter((item) => !item.label.includes("Soll"));
        setFormattedData(filteredData);
        setGlobalMax(getGlobalMax(filteredData));
    }, [data]);

    // Ermittlung der maximalen Anzahl an Datenpunkten aller Datensätze
    const maxDataLength = useMemo(() => {
        if (formattedData.length === 0) return 0;
        return Math.max(...formattedData.map((item) => item.data.length));
    }, [formattedData]);

    // Gesamtanzahl der Schritte und Animationsdauer berechnen
    const totalSteps = formattedData[0]?.data?.length || 1;
    const overallDuration = duration * totalSteps * 1000;

    // Animation: Aktualisierung des Fortschritts (time) mittels requestAnimationFrame
    useEffect(() => {
        if (!isPlaying) return;
        let animationFrame;
        let startTime = null;
        const animate = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = timestamp - startTime;
            const newTime = Math.min(progress / overallDuration, 1);
            setTime(newTime);
            if (newTime < 1) {
                animationFrame = requestAnimationFrame(animate);
            } else {
                setIsPlaying(false);
            }
        };
        animationFrame = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(animationFrame);
    }, [isPlaying, overallDuration]);

    // Berechnung des aktuellen Schrittes (als Gleitkommazahl)
    const currentStep = time * (totalSteps - 1);

    // Interpolierung der Werte je Datensatz anhand des aktuellen Schrittes
    const updatedData = formattedData.map((item) => {
        const maxIndex = item.data.length - 1;
        if (currentStep >= maxIndex) {
            return { ...item, currentValue: item.data[maxIndex] };
        }
        const lowerIndex = Math.floor(currentStep);
        const upperIndex = Math.min(lowerIndex + 1, maxIndex);
        const frac = currentStep - lowerIndex;
        const lowerValue = item.data[lowerIndex];
        const upperValue = item.data[upperIndex];
        const currentValue = lowerValue + (upperValue - lowerValue) * frac;
        return { ...item, currentValue };
    });

    // Sortierung der Datensätze nach aktuellem Wert (absteigend)
    const sortedData = [...updatedData].sort(
        (a, b) => b.currentValue - a.currentValue
    );

    // Messen der tatsächlichen Breite der linken Labels
    useLayoutEffect(() => {
        if (leftHiddenContainerRef.current) {
            const spans = leftHiddenContainerRef.current.querySelectorAll("span");
            let maxWidth = 0;
            spans.forEach((span) => {
                const w = span.getBoundingClientRect().width;
                if (w > maxWidth) {
                    maxWidth = w;
                }
            });
            setLeftLabelWidth(maxWidth);
        }
    }, [formattedData, computedFontSize]);

    // Messen der tatsächlichen Breite der rechten Texte (Werte)
    useLayoutEffect(() => {
        if (rightHiddenContainerRef.current) {
            const spans = rightHiddenContainerRef.current.querySelectorAll("span");
            let maxWidth = 0;
            spans.forEach((span) => {
                const w = span.getBoundingClientRect().width;
                if (w > maxWidth) {
                    maxWidth = w;
                }
            });
            setRightLabelWidth(maxWidth);
        }
    }, [formattedData, computedFontSize]);

    // Berechnung des linken Balkenstarts:
    // outerPadding + margin.left + maximale Labelbreite + leftGap
    useLayoutEffect(() => {
        const computedBarStart = outerPadding + margin.left + leftLabelWidth + leftGap;
        setBarStart(computedBarStart+20);
    }, [leftLabelWidth, outerPadding, margin.left, leftGap]);

    // Berechnung des rechten Balkenendes:
    // baseWidth - outerPadding - margin.right - rightGap - rightLabelWidth
    useLayoutEffect(() => {
        const computedBarEnd =
            baseWidth - outerPadding - margin.right - rightGap - rightLabelWidth;
        setBarEnd(computedBarEnd);
    }, [baseWidth, outerPadding, margin.right, rightGap, rightLabelWidth]);

    // Berechnung der verfügbaren Breite für den Balken
    useLayoutEffect(() => {
        if (barEnd > barStart) {
            setBarAreaWidth(barEnd - barStart);
        }
    }, [barEnd, barStart]);

    // D3-Skala für die Berechnung der Balkenbreiten innerhalb der verfügbaren Fläche
    const xScale = d3
        .scaleLinear()
        .domain([0, globalMax])
        .range([0, barAreaWidth]);

    // Animation der Balken mit react-spring
    const transitions = useTransition(sortedData, {
        keys: (item) => item.label,
        from: (item, index) => ({
            width: xScale(item.currentValue),
            y: index * (barHeight + barGap),
            opacity: 1,
        }),
        enter: (item, index) => ({
            width: xScale(item.currentValue),
            y: index * (barHeight + barGap),
            opacity: 1,
        }),
        update: (item, index) => ({
            width: xScale(item.currentValue),
            y: index * (barHeight + barGap),
            opacity: 1,
        }),
        config: { duration: 200 },
    });

    // Funktion zur Ermittlung des anzuzeigenden Datums oder Tages
    function getCurrentDate() {
        if (!startDate || startDate.length < 3) return "";
        const currentDate = new Date(startDate[0], startDate[1], startDate[2]);
        if (kombiniert) {
            return `Tag: ${Math.floor(currentStep + 1)}`;
        } else {
            currentDate.setDate(currentDate.getDate() + Math.floor(currentStep));
            return (
                "Datum: " +
                currentDate.toLocaleDateString("de-DE", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "2-digit",
                })
            );
        }
    }

    // Prüfen, ob ein Datensatz eine verkürzte Datenreihe hat
    const insufficientData = useMemo(
        () =>
            formattedData.some((item) => item.data.length < maxDataLength),
        [formattedData, maxDataLength]
    );

    return (
        <div
            style={{
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                padding: "1rem",
                maxWidth: "770px",
                textAlign: "center",
                borderRadius: 5,
                color: "white",
                margin: "auto",
            }}
        >
            {/* Unsichtbare Container zum Messen der linken und rechten Texte */}
            <div
                ref={leftHiddenContainerRef}
                style={{
                    position: "absolute",
                    visibility: "hidden",
                    fontSize: computedFontSize,
                    fontFamily: "sans-serif",
                }}
            >
                {formattedData.map((item) => (
                    <span key={item.label}>{item.label}</span>
                ))}
            </div>
            <div
                ref={rightHiddenContainerRef}
                style={{
                    position: "absolute",
                    visibility: "hidden",
                    fontSize: computedFontSize,
                    fontFamily: "sans-serif",
                }}
            >
                {formattedData.map((item) => {
                    // Für den rechten Text nehmen wir den letzten Wert (als Fallback)
                    const num =
                        typeof item.currentValue === "number" && !isNaN(item.currentValue)
                            ? item.currentValue
                            : item.data && item.data.length
                                ? Number(item.data[item.data.length - 1])
                                : 0;
                    return <span key={item.label}>{num.toFixed(2)} kg</span>;
                })}
            </div>

            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "1rem",
                    position: "relative",
                }}
            >
                <button
                    onClick={() => {
                        if (!isPlaying && time >= 1) setTime(0);
                        setIsPlaying(!isPlaying);
                    }}
                    style={{
                        padding: "0.3rem 0.6rem",
                        cursor: "pointer",
                        minWidth: 60,
                        position: "absolute",
                        left: 0,
                        top: 0,
                    }}
                    className="chart-button"
                >
                    {isPlaying ? "Pause" : "Play"}
                </button>
                <h3 style={{ margin: "auto", fontSize: 14 }}>{getCurrentDate()}</h3>
            </div>

            <svg
                style={{ width: "100%", height: "auto" }}
                viewBox={`0 0 ${baseWidth} ${
                    outerPadding * 2 +
                    margin.top +
                    margin.bottom +
                    sortedData.length * barHeight +
                    (sortedData.length - 1) * barGap
                }`}
            >
                {transitions((style, item) => {
                    // Falls der Datensatz eine verkürzte Datenreihe hat, wird ein "*" an das Label gehängt
                    const displayLabel =
                        item.data.length < maxDataLength ? `${item.label}*` : item.label;
                    return (
                        <animated.g
                            key={item.label}
                            transform={style.y.to(
                                (y) => `translate(0, ${outerPadding + margin.top + y})`
                            )}
                            opacity={style.opacity}
                        >
                            {/* Linker Text (Label): Immer linksbündig beginnend bei outerPadding + margin.left */}
                            <text
                                x={0}
                                y={barHeight / 2}
                                fontSize={computedFontSize}
                                textAnchor="start"
                                alignmentBaseline="middle"
                                fill="#fff"
                            >
                                {displayLabel}
                            </text>

                            {/* Balken: Beginnt immer bei barStart */}
                            <animated.rect
                                x={barStart}
                                y={0}
                                height={barHeight}
                                fill={item.backgroundColor}
                                stroke={item.borderColor}
                                strokeWidth={1}
                                width={style.width}
                            />

                            {/* Rechter Text (Wert): Rechtsbündig bei baseWidth - outerPadding - margin.right */}
                            <text
                                x={baseWidth}
                                y={barHeight / 2}
                                fontSize={computedFontSize}
                                textAnchor="end"
                                alignmentBaseline="middle"
                                fill="#fff"
                            >
                                {(
                                    (typeof item.currentValue === "number" &&
                                    !isNaN(item.currentValue)
                                        ? item.currentValue
                                        : Array.isArray(item.data) && item.data.length
                                            ? Number(item.data[item.data.length - 1])
                                            : 0) || 0
                                ).toFixed(2)}{" "}
                                kg
                            </text>
                        </animated.g>
                    );
                })}
            </svg>
            {insufficientData && (
                <div style={{ marginTop: "1rem", fontSize: "0.9rem", color: "#ccc" }}>
                    *ungenügend Daten
                </div>
            )}
        </div>
    );
}

export default BarChartRace;
